import { useState, useEffect } from 'react'
import { useForm, UseFormReturn } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useFormChangeTracking,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { FloatingActionIslandContent } from '@/components/ui/FloatingActionIslandContent'

const createBaseFormSchema = (requireEmail: boolean) =>
  z.object({
    accountIdentifier: requireEmail
      ? z.string().email('Please enter a valid email address')
      : z.string().min(1, 'Username is required'),
    password: z.string().min(1, 'Password is required'),
  })

type BaseFormValues = z.infer<ReturnType<typeof createBaseFormSchema>>

// Extended form values can include any additional fields
type ExtendedFormValues = BaseFormValues & Record<string, unknown>

interface AdditionalFieldsRenderProps {
  form: UseFormReturn<ExtendedFormValues>
  isSubmitting: boolean
}

interface AccountCredentialFormProps {
  onSubmit: (data: ExtendedFormValues) => Promise<void>
  accountIdentifierLabel?: string
  accountIdentifierPlaceholder?: string
  accountIdentifierHelp?: string
  passwordLabel?: string
  passwordPlaceholder?: string
  passwordHelp?: string
  submitButtonText?: string
  initialAccountIdentifier?: string
  confirmationText?: string
  children?: (props: AdditionalFieldsRenderProps) => React.ReactNode
  additionalSchema?: z.ZodObject<z.ZodRawShape>
  initialValues?: Record<string, unknown>
  requireEmail?: boolean
}

export function AccountCredentialForm({
  onSubmit,
  accountIdentifierLabel = 'Email',
  accountIdentifierPlaceholder = 'your-email@example.com',
  accountIdentifierHelp,
  passwordLabel = 'Password',
  passwordPlaceholder = 'Enter your password',
  passwordHelp,
  submitButtonText = 'Connect',
  initialAccountIdentifier,
  confirmationText,
  children,
  additionalSchema,
  initialValues,
  requireEmail = true,
}: AccountCredentialFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const baseFormSchema = createBaseFormSchema(requireEmail)
  const formSchema = additionalSchema
    ? baseFormSchema.merge(additionalSchema)
    : baseFormSchema

  const form = useForm({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
    defaultValues: {
      accountIdentifier: initialAccountIdentifier || '',
      password: '',
      ...(initialValues ?? {}),
    },
  }) as unknown as UseFormReturn<ExtendedFormValues>

  useEffect(() => {
    if (initialAccountIdentifier || initialValues) {
      form.reset({
        accountIdentifier: initialAccountIdentifier || '',
        password: '',
        ...(initialValues ?? {}),
      })
    }
  }, [initialAccountIdentifier, initialValues, form])

  const FloatingIslandContent = () => {
    const { hasUnsavedChanges } = useFormChangeTracking()

    return (
      <FloatingActionIslandContent
        show={hasUnsavedChanges}
        componentId="account-credential-form"
        priority={20}
      >
        <div className="inline-flex w-full max-w-2xl">
          {confirmationText && (
            <div className="flex flex-grow items-center space-x-2 text-sm text-gray-600 whitespace-nowrap pointer-events-none mr-4">
              <span>{confirmationText}</span>
            </div>
          )}

          <div className="flex items-center space-x-2 justify-end pointer-events-auto ml-auto">
            <Button
              onClick={form.handleSubmit(handleFormSubmit)}
              disabled={isSubmitting}
              size="sm"
              variant="form"
            >
              <span>{isSubmitting ? 'Connecting...' : submitButtonText}</span>
            </Button>
          </div>
        </div>
      </FloatingActionIslandContent>
    )
  }

  const handleFormSubmit = async (values: ExtendedFormValues) => {
    try {
      setIsSubmitting(true)
      await onSubmit(values)
      form.reset(values)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleFormSubmit)}
        noValidate
        className="space-y-6 max-w-2xl"
      >
        <FormField
          control={form.control}
          name="accountIdentifier"
          render={({ field }) => (
            <FormItem className="max-w-md">
              <FormLabel>{accountIdentifierLabel}</FormLabel>
              <FormControl>
                <Input
                  placeholder={accountIdentifierPlaceholder}
                  disabled={isSubmitting}
                  autoFocus
                  {...field}
                />
              </FormControl>
              {accountIdentifierHelp && (
                <FormDescription>{accountIdentifierHelp}</FormDescription>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem className="max-w-md">
              <FormLabel>{passwordLabel}</FormLabel>
              <FormControl>
                <Input
                  type="password"
                  placeholder={passwordPlaceholder}
                  disabled={isSubmitting}
                  {...field}
                />
              </FormControl>
              {passwordHelp && (
                <FormDescription>{passwordHelp}</FormDescription>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        {children?.({ form, isSubmitting })}
      </form>

      <FloatingIslandContent />
    </Form>
  )
}
